import { useEffect, useState } from "react";
import { ResponsiveContainer, BarChart, Bar, XAxis, Tooltip, LineChart, Line, YAxis } from "recharts";
import { AlertTriangle } from "lucide-react";
import { api, inr, fmtDate, Stat, Badge } from "./shared";

export const AdminDashboard = ({ onUnauthorized, onOpenOrder }) => {
    const [s, setS] = useState(null);
    useEffect(() => {
        api.get("/admin/stats").then(setS).catch((e) => e.response?.status === 401 && onUnauthorized());
    }, [onUnauthorized]);
    if (!s) return <div className="grid sm:grid-cols-4 gap-4">{[...Array(8)].map((_, i) => <div key={i} className="h-24 bg-cream/60 animate-pulse" />)}</div>;
    const daily = s.daily.map((d) => ({ ...d, label: d.date.slice(5) }));
    return (
        <div data-testid="admin-dashboard-tab" className="space-y-10">
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                <Stat label="Total sales" value={inr(s.total_sales)} testId="stat-total-sales" />
                <Stat label="Today's sales" value={inr(s.today_sales)} sub={`${s.today_orders} order${s.today_orders === 1 ? "" : "s"} today`} testId="stat-today-sales" />
                <Stat label="Total orders" value={s.total_orders} sub={`${s.awaiting_payment} awaiting payment`} testId="stat-total-orders" />
                <Stat label="Customers" value={s.total_customers} testId="stat-customers" />
                <Stat label="New orders" value={s.new_orders} testId="stat-new" />
                <Stat label="Pending" value={s.pending_orders} sub="Confirmed · Processing · Packed" testId="stat-pending" />
                <Stat label="Shipped" value={s.shipped_orders} testId="stat-shipped" />
                <Stat label="Delivered" value={s.delivered_orders} sub={`${s.cancelled_orders} cancelled / returned`} testId="stat-delivered" />
            </div>

            <div className="grid lg:grid-cols-2 gap-6">
                <div className="border border-gold/25 bg-cream/30 p-6" data-testid="chart-sales">
                    <p className="text-[10px] tracking-[0.25em] uppercase text-ash mb-4">Sales · last 14 days</p>
                    <div className="h-48 min-w-[200px]">
                        <ResponsiveContainer width="100%" height="100%">
                            <BarChart data={daily} margin={{ left: 0, right: 0, top: 4, bottom: 0 }}>
                                <XAxis dataKey="label" tick={{ fontSize: 10, fill: "#8C8577" }} axisLine={false} tickLine={false} interval={1} />
                                <YAxis hide />
                                <Tooltip cursor={{ fill: "rgba(197,160,89,0.12)" }} contentStyle={{ background: "#1A1814", border: "1px solid #C5A059", borderRadius: 0, fontSize: 12, color: "#DFCC9D" }} formatter={(v) => [inr(v), "Sales"]} />
                                <Bar dataKey="sales" fill="#B8963E" radius={0} />
                            </BarChart>
                        </ResponsiveContainer>
                    </div>
                </div>
                <div className="border border-gold/25 bg-cream/30 p-6" data-testid="chart-orders">
                    <p className="text-[10px] tracking-[0.25em] uppercase text-ash mb-4">Orders · last 14 days</p>
                    <div className="h-48 min-w-[200px]">
                        <ResponsiveContainer width="100%" height="100%">
                            <LineChart data={daily} margin={{ left: 0, right: 0, top: 4, bottom: 0 }}>
                                <XAxis dataKey="label" tick={{ fontSize: 10, fill: "#8C8577" }} axisLine={false} tickLine={false} interval={1} />
                                <YAxis hide allowDecimals={false} />
                                <Tooltip contentStyle={{ background: "#1A1814", border: "1px solid #C5A059", borderRadius: 0, fontSize: 12, color: "#DFCC9D" }} formatter={(v) => [v, "Orders"]} />
                                <Line type="monotone" dataKey="orders" stroke="#1A1814" strokeWidth={1.5} dot={{ r: 2, fill: "#B8963E", stroke: "#B8963E" }} />
                            </LineChart>
                        </ResponsiveContainer>
                    </div>
                </div>
            </div>

            <div className="grid lg:grid-cols-[1.4fr_1fr] gap-6">
                <div className="border border-gold/25" data-testid="recent-orders">
                    <p className="text-[10px] tracking-[0.25em] uppercase text-ash px-5 py-4 border-b border-gold/15">Recent orders</p>
                    <ul className="divide-y divide-gold/15">
                        {s.recent_orders.map((o) => (
                            <li key={o.id}>
                                <button onClick={() => onOpenOrder(o.id)} data-testid={`recent-order-${o.id}`} className="w-full text-left px-5 py-3 flex items-center justify-between gap-4 hover:bg-cream/50 transition-colors">
                                    <div>
                                        <p className="text-sm text-charcoal">#{o.ref} · {o.name}</p>
                                        <p className="text-[11px] text-ash">{fmtDate(o.created_at)} · {o.qty} item{o.qty === 1 ? "" : "s"}</p>
                                    </div>
                                    <div className="flex items-center gap-3">
                                        <span className="font-serif text-charcoal">{inr(o.total)}</span>
                                        <Badge status={o.status} />
                                    </div>
                                </button>
                            </li>
                        ))}
                        {s.recent_orders.length === 0 && <li className="px-5 py-10 text-center font-serif text-xl text-ash italic">No orders yet</li>}
                    </ul>
                </div>
                <div className="border border-gold/25" data-testid="low-stock">
                    <p className="text-[10px] tracking-[0.25em] uppercase text-ash px-5 py-4 border-b border-gold/15 flex items-center gap-2"><AlertTriangle size={12} className="text-golddark" /> Low stock (&lt; 10)</p>
                    <ul className="divide-y divide-gold/15">
                        {s.low_stock.map((p) => (
                            <li key={p.name} className="px-5 py-3 flex justify-between text-sm"><span className="text-charcoal">{p.name}</span><span className={p.stock === 0 ? "text-[#8B2E2E]" : "text-golddark"}>{p.stock} left</span></li>
                        ))}
                        {s.low_stock.length === 0 && <li className="px-5 py-10 text-center font-serif text-xl text-ash italic">All products well stocked</li>}
                    </ul>
                </div>
            </div>
        </div>
    );
};
